if (!window.customElements.get('sizing-guide-zoom')) {
  class SizingGuideZoom extends HTMLElement {
    connectedCallback() {
      this.minScale = 1;
      this.maxScale = 4;
      this.scaleStep = 0.25;
      this.scale = 1;
      this.translateX = 0;
      this.translateY = 0;
      this.isDragging = false;
      this.lastPointer = null;
      this.pointers = new Map();
      this.lastPinchDistance = 0;

      this.onWheel = this.onWheel.bind(this);
      this.onPointerDown = this.onPointerDown.bind(this);
      this.onPointerMove = this.onPointerMove.bind(this);
      this.onPointerUp = this.onPointerUp.bind(this);
      this.onDoubleClick = this.onDoubleClick.bind(this);
      this.onControlClick = this.onControlClick.bind(this);
      this.onModalHide = this.onModalHide.bind(this);

      // Script may evaluate before children are parsed; defer setup one frame.
      this.initFrame = requestAnimationFrame(() => this.setup());
    }

    setup() {
      this.viewport = this.querySelector('[data-sizing-guide-viewport]');
      this.image = this.querySelector('img');

      if (!this.viewport || !this.image) {
        return;
      }

      this.viewport.addEventListener('wheel', this.onWheel, { passive: false });
      this.viewport.addEventListener('pointerdown', this.onPointerDown);
      this.viewport.addEventListener('pointermove', this.onPointerMove);
      this.viewport.addEventListener('pointerup', this.onPointerUp);
      this.viewport.addEventListener('pointercancel', this.onPointerUp);
      this.viewport.addEventListener('pointerleave', this.onPointerUp);
      this.viewport.addEventListener('dblclick', this.onDoubleClick);
      this.addEventListener('click', this.onControlClick);

      this.modal = this.closest('x-modal');
      if (this.modal) {
        this.modal.addEventListener('dialog:after-hide', this.onModalHide);
      }

      this.applyTransform();
      this.updateControls();
    }

    disconnectedCallback() {
      if (this.initFrame) {
        cancelAnimationFrame(this.initFrame);
      }

      if (!this.viewport) {
        return;
      }

      this.viewport.removeEventListener('wheel', this.onWheel);
      this.viewport.removeEventListener('pointerdown', this.onPointerDown);
      this.viewport.removeEventListener('pointermove', this.onPointerMove);
      this.viewport.removeEventListener('pointerup', this.onPointerUp);
      this.viewport.removeEventListener('pointercancel', this.onPointerUp);
      this.viewport.removeEventListener('pointerleave', this.onPointerUp);
      this.viewport.removeEventListener('dblclick', this.onDoubleClick);
      this.removeEventListener('click', this.onControlClick);

      if (this.modal) {
        this.modal.removeEventListener('dialog:after-hide', this.onModalHide);
      }
    }

    onModalHide() {
      this.reset();
    }

    onControlClick(event) {
      const control = event.target.closest('[data-sizing-guide-zoom]');

      if (!control) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      const action = control.getAttribute('data-sizing-guide-zoom');

      if (action === 'in') {
        this.zoomAt(this.scale + this.scaleStep);
      } else if (action === 'out') {
        this.zoomAt(this.scale - this.scaleStep);
      } else if (action === 'reset') {
        this.reset();
      }
    }

    onWheel(event) {
      event.preventDefault();

      const delta = event.deltaY > 0 ? -this.scaleStep : this.scaleStep;
      const rect = this.viewport.getBoundingClientRect();
      const originX = event.clientX - rect.left;
      const originY = event.clientY - rect.top;

      this.zoomAt(this.scale + delta, originX, originY);
    }

    onDoubleClick(event) {
      const rect = this.viewport.getBoundingClientRect();
      const originX = event.clientX - rect.left;
      const originY = event.clientY - rect.top;

      if (this.scale > 1.05) {
        this.reset();
      } else {
        this.zoomAt(2, originX, originY);
      }
    }

    onPointerDown(event) {
      if (event.target.closest('[data-sizing-guide-zoom]')) {
        return;
      }

      this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
      this.viewport.setPointerCapture?.(event.pointerId);

      if (this.pointers.size === 1) {
        this.isDragging = this.scale > 1;
        this.lastPointer = { x: event.clientX, y: event.clientY };
        this.viewport.classList.toggle('is-dragging', this.isDragging);
      } else if (this.pointers.size === 2) {
        this.isDragging = false;
        this.viewport.classList.remove('is-dragging');
        this.lastPinchDistance = this.getPinchDistance();
      }
    }

    onPointerMove(event) {
      if (!this.pointers.has(event.pointerId)) {
        return;
      }

      this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });

      if (this.pointers.size === 2) {
        const distance = this.getPinchDistance();

        if (this.lastPinchDistance > 0) {
          const ratio = distance / this.lastPinchDistance;
          const center = this.getPinchCenter();
          const rect = this.viewport.getBoundingClientRect();

          this.zoomAt(this.scale * ratio, center.x - rect.left, center.y - rect.top);
        }

        this.lastPinchDistance = distance;
        return;
      }

      if (!this.isDragging || !this.lastPointer) {
        return;
      }

      const dx = event.clientX - this.lastPointer.x;
      const dy = event.clientY - this.lastPointer.y;

      this.translateX += dx;
      this.translateY += dy;
      this.lastPointer = { x: event.clientX, y: event.clientY };

      this.clampPan();
      this.applyTransform();
    }

    onPointerUp(event) {
      this.pointers.delete(event.pointerId);

      if (this.pointers.size < 2) {
        this.lastPinchDistance = 0;
      }

      if (this.pointers.size === 0) {
        this.isDragging = false;
        this.lastPointer = null;
        this.viewport.classList.remove('is-dragging');
      } else if (this.pointers.size === 1) {
        const remaining = this.pointers.values().next().value;
        this.lastPointer = { x: remaining.x, y: remaining.y };
        this.isDragging = this.scale > 1;
        this.viewport.classList.toggle('is-dragging', this.isDragging);
      }
    }

    getPinchDistance() {
      const points = Array.from(this.pointers.values());

      if (points.length < 2) {
        return 0;
      }

      const dx = points[0].x - points[1].x;
      const dy = points[0].y - points[1].y;

      return Math.hypot(dx, dy);
    }

    getPinchCenter() {
      const points = Array.from(this.pointers.values());

      return {
        x: (points[0].x + points[1].x) / 2,
        y: (points[0].y + points[1].y) / 2
      };
    }

    zoomAt(nextScale, originX, originY) {
      const previousScale = this.scale;
      const clampedScale = Math.min(this.maxScale, Math.max(this.minScale, nextScale));

      if (clampedScale === previousScale) {
        this.updateControls();
        return;
      }

      const rect = this.viewport.getBoundingClientRect();
      const focusX = originX ?? rect.width / 2;
      const focusY = originY ?? rect.height / 2;
      const centerX = rect.width / 2;
      const centerY = rect.height / 2;
      const ratio = clampedScale / previousScale;

      this.scale = clampedScale;
      this.translateX = focusX - centerX - (focusX - centerX - this.translateX) * ratio;
      this.translateY = focusY - centerY - (focusY - centerY - this.translateY) * ratio;

      if (this.scale === 1) {
        this.translateX = 0;
        this.translateY = 0;
      } else {
        this.clampPan();
      }

      this.applyTransform();
      this.updateControls();
    }

    clampPan() {
      if (this.scale <= 1) {
        this.translateX = 0;
        this.translateY = 0;
        return;
      }

      const rect = this.viewport.getBoundingClientRect();
      const maxX = (rect.width * (this.scale - 1)) / 2 + rect.width * 0.15;
      const maxY = (rect.height * (this.scale - 1)) / 2 + rect.height * 0.15;

      this.translateX = Math.min(maxX, Math.max(-maxX, this.translateX));
      this.translateY = Math.min(maxY, Math.max(-maxY, this.translateY));
    }

    applyTransform() {
      this.image.style.transform = `translate3d(${this.translateX}px, ${this.translateY}px, 0) scale(${this.scale})`;
      this.viewport.classList.toggle('is-zoomed', this.scale > 1.01);
      this.viewport.style.cursor = this.scale > 1 ? 'grab' : 'zoom-in';
    }

    updateControls() {
      const zoomIn = this.querySelector('[data-sizing-guide-zoom="in"]');
      const zoomOut = this.querySelector('[data-sizing-guide-zoom="out"]');
      const reset = this.querySelector('[data-sizing-guide-zoom="reset"]');

      if (zoomIn) {
        zoomIn.disabled = this.scale >= this.maxScale - 0.001;
      }

      if (zoomOut) {
        zoomOut.disabled = this.scale <= this.minScale + 0.001;
      }

      if (reset) {
        reset.disabled = this.scale <= this.minScale + 0.001;
      }
    }

    reset() {
      this.scale = 1;
      this.translateX = 0;
      this.translateY = 0;
      this.applyTransform();
      this.updateControls();
    }
  }

  window.customElements.define('sizing-guide-zoom', SizingGuideZoom);
}
